/**
 * Das Dashboard (KONZEPT.md §10, §14.2) -- Kennzahlen des Core und Kacheln
 * der Module.
 *
 * **Module liefern Daten, keine Oberflaeche.** Eine Kachel ist ein Dokument in
 * einer von vier festen Formen; gezeichnet wird sie von der Oberflaeche. So
 * sieht jede Kachel aus wie die anderen, funktioniert im Dunkelmodus und mit
 * Handschuhen, und es wird kein fremder Code nachgeladen (KONZEPT.md §3.6).
 */
import { z } from "zod";
import { Id, KurzText, Zeitpunkt } from "./basis.js";
import { Verbleib } from "./enums.js";
// --- Kacheln der Module -------------------------------------------------------
/** Einschaetzung einer Zahl: gibt der Kachel Farbe, Symbol und Wort. */
export const KachelTon = z.enum(["neutral", "gut", "warnung", "kritisch"]);
/** Ein Ziel innerhalb der Oberflaeche, etwa `/artikel/<id>`. Nie eine fremde Adresse. */
const InternerLink = z.string().regex(/^\/[^/]/, "Nur Pfade innerhalb der Anwendung");
export const DashboardKachel = z.discriminatedUnion("art", [
    /** Ein paar Zahlen nebeneinander. */
    z.object({
        art: z.literal("kennzahlen"),
        titel: KurzText,
        eintraege: z
            .array(z.object({
            bezeichnung: KurzText,
            wert: z.number(),
            einheit: z.string().max(20).optional(),
            ton: KachelTon.default("neutral"),
            link: InternerLink.optional(),
        }))
            .min(1)
            .max(8),
    }),
    /** Wie sich eine Menge aufteilt -- als Balken gezeichnet. */
    z.object({
        art: z.literal("verteilung"),
        titel: KurzText,
        eintraege: z
            .array(z.object({
            bezeichnung: KurzText,
            wert: z.number().nonnegative(),
            link: InternerLink.optional(),
        }))
            .max(20),
    }),
    /** Ein Verlauf ueber Tage. */
    z.object({
        art: z.literal("zeitreihe"),
        titel: KurzText,
        einheit: z.string().max(20).optional(),
        punkte: z.array(z.object({ datum: z.iso.date(), wert: z.number() })).max(366),
    }),
    /** Eine kurze Liste, etwa „als Naechstes zurueckzugeben". */
    z.object({
        art: z.literal("liste"),
        titel: KurzText,
        zeilen: z
            .array(z.object({
            text: KurzText,
            zusatz: z.string().max(200).optional(),
            ton: KachelTon.default("neutral"),
            link: InternerLink.optional(),
        }))
            .max(20),
        leer: z.string().max(200).default("Nichts vorhanden."),
    }),
]);
/**
 * Eintrag im Modul-Manifest: „Unter diesem Pfad liefere ich eine Kachel."
 *
 * Der Pfad ist relativ zum Praefix des Moduls (`/api/m/<modul>`). Der Core
 * sammelt die Eintraege und nennt sie der Oberflaeche unter `/api/core/module`.
 */
export const DashboardEintrag = z.object({
    id: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/),
    titel: KurzText,
    pfad: z.string().regex(/^\/[a-z0-9/_-]*$/, "Pfad innerhalb des Moduls, etwa /kennzahlen"),
});
// --- Kennzahlen des Core ------------------------------------------------------
/** Zeitraum der Buchungsbewegungen. */
export const KennzahlenAnfrage = z.object({
    tage: z.coerce.number().int().min(7).max(366).default(30),
});
export const Kennzahlen = z.object({
    stand: Zeitpunkt,
    /** Der aktive Bestand -- ohne archivierte Artikel (KONZEPT.md §7.2). */
    bestand: z.object({
        gesamt: z.number().int().nonnegative(),
        /** Einsatzbereit, im Lager und nicht archiviert -- greifbar. */
        verfuegbar: z.number().int().nonnegative(),
        /** Der Status zaehlt als einsatzbereit, gleich wo der Artikel ist. */
        einsatzbereit: z.number().int().nonnegative(),
        archiviert: z.number().int().nonnegative(),
        nach_status: z.array(z.object({ status: z.string(), anzahl: z.number().int() })),
        nach_verbleib: z.array(z.object({ verbleib: Verbleib, anzahl: z.number().int() })),
    }),
    /**
     * Buchungen je Tag, ohne die vom Core abgeleiteten (sonst zaehlte eine
     * umgelagerte Gitterbox mit dreissig Teilen dreissigmal).
     */
    bewegungen: z.object({
        tage: z.number().int(),
        reihe: z.array(z.object({
            datum: z.iso.date(),
            bewegungen: z.number().int(),
            statusaenderungen: z.number().int(),
        })),
    }),
    /** Die Pruefampel (KONZEPT.md §14.2), je Artikel nach seiner naechsten Pruefung. */
    pruefungen: z.object({
        ueberfaellig: z.number().int(),
        bald_faellig: z.number().int(),
        in_ordnung: z.number().int(),
        ohne_termin: z.number().int(),
        /** Frist fuer „bald faellig", in Tagen. */
        frist_tage: z.number().int(),
        naechste: z.array(z.object({
            artikel_id: Id,
            inventarnummer: z.string(),
            bezeichnung: z.string(),
            pruefung: z.string(),
            faellig: z.iso.date(),
        })),
    }),
});
//# sourceMappingURL=dashboard.js.map